/**
 * Profile tab of the Admin User Editor (AR-327) — identity, contact details,
 * the system IDs that tie the member to TRAMS/HubSpot/GDS, and their public
 * web presence.
 */
import React, { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Plus, Shield } from 'lucide-react';
import {
  DeferredAction,
  FieldGrid,
  ReadOnlyField,
  RecordCard,
  SelectField,
  TextField,
} from './recordPrimitives';
import type { AdminUserRecord } from '@/lib/adminUserRecordApi';
import type { RecordDraft } from './useRecordDraft';

export const SALUTATIONS = ['Mrs', 'Ms', 'Mr', 'Dr'];
// Stored as a slug on `user_profiles.employment_type` (AR-382); nothing wrote
// the column before, so there is no legacy vocabulary to honour.
export const EMPLOYMENT_TYPES = [
  { value: 'full_time', label: 'Full Time' },
  { value: 'part_time', label: 'Part Time' },
];
export const CONTACT_TYPES = ['Agency Owner', 'Associate Advisor'];
export const ACCOUNT_STATUSES = [
  'Checkout Pending',
  'Pending',
  'Active',
  'Suspended',
  'Deactivated',
];
export const GDS_OPTIONS = ['No', 'Sabre', 'Worldspan', 'Multiple'];
export const TSHIRT_SIZES = [
  ...['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'].map((size) => `${size} (Women's)`),
  ...['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'].map((size) => `${size} (Men's)`),
];
// Standard-time offsets. The draft design paired each region with the offset
// one zone to its east; these are the real ones.
export const TIME_ZONES = [
  'UTC −05:00 US Eastern',
  'UTC −06:00 US Central',
  'UTC −07:00 US Mountain',
  'UTC −08:00 US Pacific',
  'UTC −07:00 US Arizona',
  'UTC −09:00 US Alaska',
  'UTC −10:00 Pacific Honolulu',
  'UTC −11:00 Pacific Midway',
  'UTC −04:00 Canada Atlantic',
];

const ROLE_SLUGS = [
  'AGENCY_OWNER',
  'AGENCY_MANAGER',
  'ADVISOR',
  'READ_ONLY',
  'ADMIN',
  'SYSTEM_ADMIN',
];

const ROLE_LABELS: Record<string, string> = {
  AGENCY_OWNER: 'Agency Owner',
  AGENCY_MANAGER: 'Agency Manager',
  ADVISOR: 'Associate Advisor',
  READ_ONLY: 'Read Only',
  ADMIN: 'Admin',
  SYSTEM_ADMIN: 'System Admin',
};

export const roleLabel = (slug: string | null | undefined): string =>
  (slug && ROLE_LABELS[slug]) || slug || 'Unassigned';

/** The role slug stays the stored value; the admin reads the label. */
const ROLE_OPTIONS = ROLE_SLUGS.map((slug) => ({ value: slug, label: roleLabel(slug) }));

type ActionKey =
  | 'approve-photo'
  | 'decline-photo'
  | 'replace-photo'
  | 'status-change'
  | 'showcase-toggle'
  | null;

function AddressBlock({
  heading,
  prefix,
  draft,
}: {
  heading: string;
  prefix: 'billing' | 'mailing';
  draft: RecordDraft;
}) {
  return (
    <div>
      <p className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">{heading}</p>
      <FieldGrid cols={2}>
        <TextField
          label="Street"
          value={draft.text(`${prefix}.street`)}
          readOnly={!draft.editable(`${prefix}.street`)}
          onChange={draft.setText(`${prefix}.street`)}
        />
        <TextField
          label="Secondary Line"
          placeholder="Apt, suite, etc."
          value={draft.text(`${prefix}.line2`)}
          readOnly={!draft.editable(`${prefix}.line2`)}
          onChange={draft.setText(`${prefix}.line2`)}
        />
        <TextField
          label="City"
          value={draft.text(`${prefix}.city`)}
          readOnly={!draft.editable(`${prefix}.city`)}
          onChange={draft.setText(`${prefix}.city`)}
        />
        <TextField
          label="State"
          value={draft.text(`${prefix}.state`)}
          readOnly={!draft.editable(`${prefix}.state`)}
          onChange={draft.setText(`${prefix}.state`)}
        />
        <TextField
          label="ZIP"
          value={draft.text(`${prefix}.zip`)}
          readOnly={!draft.editable(`${prefix}.zip`)}
          onChange={draft.setText(`${prefix}.zip`)}
        />
      </FieldGrid>
    </div>
  );
}

export function ProfileTab({
  record,
  draft,
  onManageAccess,
}: {
  record: AdminUserRecord;
  draft: RecordDraft;
  onManageAccess: () => void;
}) {
  const [action, setAction] = useState<ActionKey>(null);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const showcaseLink = draft.text('profile.advisorShowcase');
  const showcasePublished = draft.bool('profile.showcasePublished');
  const websites = draft.list('profile.websites');

  return (
    <div className="flex flex-col gap-5">
      <RecordCard
        title="Profile Photo"
        description="PNG or JPG, up to 4 MB. Shown at 310 × 310 on the Advisor Showcase."
      >
        <div className="flex flex-wrap items-start gap-5">
          <Avatar className="h-32 w-32 rounded-xl">
            <AvatarImage src={record.user.avatarUrl ?? undefined} alt={record.user.displayName} />
            <AvatarFallback className="rounded-xl text-2xl">
              {record.user.displayName.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col items-start gap-2">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Admins approve, decline, or replace the member&rsquo;s Showcase image.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setAction('approve-photo')}>
                Approve
              </Button>
              <Button size="sm" variant="outline" onClick={() => setAction('decline-photo')}>
                Decline
              </Button>
              <Button size="sm" variant="outline" onClick={() => setAction('replace-photo')}>
                Replace Image
              </Button>
            </div>
            <label className="mt-2 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
              <Checkbox
                checked={draft.bool('user.isTestAccount')}
                onCheckedChange={(checked) => draft.set('user.isTestAccount', checked === true)}
              />
              Test Account — removed from data sets and skipped by checks
            </label>
          </div>
        </div>
      </RecordCard>

      <RecordCard title="Identity">
        <FieldGrid>
          <TextField
            label="First Name"
            value={draft.text('profile.first')}
            readOnly={!draft.editable('profile.first')}
            onChange={draft.setText('profile.first')}
          />
          <TextField
            label="Last Name"
            value={draft.text('profile.last')}
            readOnly={!draft.editable('profile.last')}
            onChange={draft.setText('profile.last')}
          />
          <TextField
            label="Nickname"
            value={draft.text('profile.nickname')}
            readOnly={!draft.editable('profile.nickname')}
            onChange={draft.setText('profile.nickname')}
          />
          <TextField
            label="Member Full Legal Name"
            value={draft.text('profile.legalName')}
            readOnly={!draft.editable('profile.legalName')}
            onChange={draft.setText('profile.legalName')}
          />
          <TextField
            label="Account Email"
            type="email"
            value={draft.text('user.email')}
            readOnly
          />
          <SelectField
            label="Salutation"
            value={draft.text('profile.salutation')}
            readOnly={!draft.editable('profile.salutation')}
            options={SALUTATIONS}
            onChange={draft.setText('profile.salutation')}
          />
          <SelectField
            label="Full / Part Time"
            value={draft.text('profile.employment')}
            readOnly={!draft.editable('profile.employment')}
            options={EMPLOYMENT_TYPES}
            onChange={draft.setText('profile.employment')}
          />
          <SelectField
            label="Contact Type"
            value={draft.text('profile.contactType')}
            readOnly={!draft.editable('profile.contactType')}
            options={CONTACT_TYPES}
            onChange={draft.setText('profile.contactType')}
          />
          <SelectField
            label="T-Shirt Size"
            value={draft.text('profile.tshirtSize')}
            readOnly={!draft.editable('profile.tshirtSize')}
            options={TSHIRT_SIZES}
            onChange={draft.setText('profile.tshirtSize')}
          />
          <SelectField
            label="Account Status"
            value={draft.text('user.accountStatus')}
            options={ACCOUNT_STATUSES}
            onChange={(next) => {
              setPendingStatus(next);
              setAction('status-change');
            }}
          />
          <SelectField
            label="Permission Role"
            value={draft.text('user.roleSlug')}
            readOnly={!draft.editable('user.roleSlug')}
            options={ROLE_OPTIONS}
            onChange={draft.setText('user.roleSlug')}
          />
        </FieldGrid>

        <Button variant="outline" size="sm" className="mt-4" onClick={onManageAccess}>
          <Shield className="mr-2 h-4 w-4" aria-hidden="true" />
          Manage Role + Access
        </Button>

        <div className="mt-4 flex flex-col gap-1.5">
          <Label htmlFor="member-bio" className="text-xs font-medium text-slate-500 dark:text-slate-400">
            Professional Bio <span className="text-slate-400">(read-only)</span>
          </Label>
          <Textarea
            id="member-bio"
            readOnly
            value={record.profile?.bio ?? ''}
            placeholder="No bio on the member profile."
            className="min-h-[90px] bg-slate-50 text-sm text-slate-600 dark:bg-gray-900"
          />
        </div>
      </RecordCard>

      <RecordCard title="Contact Info">
        <div className="flex flex-col gap-5">
          <AddressBlock heading="Billing Address" prefix="billing" draft={draft} />
          <Separator />
          <AddressBlock heading="Mailing Address" prefix="mailing" draft={draft} />
          <Separator />
          <FieldGrid>
            <SelectField
              label="Time Zone"
              value={draft.text('profile.timezone')}
              readOnly={!draft.editable('profile.timezone')}
              options={TIME_ZONES}
              onChange={draft.setText('profile.timezone')}
            />
            <TextField
              label="Primary Phone"
              value={draft.text('profile.phone')}
              readOnly={!draft.editable('profile.phone')}
              onChange={draft.setText('profile.phone')}
            />
            <TextField
              label="Mobile Phone"
              placeholder="Mobile phone"
              value={draft.text('profile.mobilePhone')}
              readOnly={!draft.editable('profile.mobilePhone')}
              onChange={draft.setText('profile.mobilePhone')}
            />
          </FieldGrid>
        </div>
      </RecordCard>

      <RecordCard
        title="System IDs"
        description={
          draft.bound
            ? 'Read-only — HubSpot, TRAMS, and TRIO own these links; TRAMS IDs are the finance join key.'
            : 'Identity links written back to HubSpot, TRAMS, and TRIO on save.'
        }
      >
        <FieldGrid>
          <TextField
            label="HubSpot ID"
            value={draft.text('ids.hubspot')}
            readOnly={!draft.editable('ids.hubspot')}
            onChange={draft.setText('ids.hubspot')}
          />
          <TextField
            label="TRAMS Interface ID"
            value={draft.text('ids.tramsInterface')}
            readOnly={!draft.editable('ids.tramsInterface')}
            onChange={draft.setText('ids.tramsInterface')}
          />
          <TextField
            label="TRAMS Agent Name"
            value={draft.text('ids.tramsAgentName')}
            readOnly={!draft.editable('ids.tramsAgentName')}
            onChange={draft.setText('ids.tramsAgentName')}
          />
          <TextField
            label="TRAMS Profile ID"
            value={draft.text('ids.tramsProfileId')}
            readOnly={!draft.editable('ids.tramsProfileId')}
            onChange={draft.setText('ids.tramsProfileId')}
          />
          <TextField
            label="TRAMS Corporate ID"
            value={draft.text('ids.tramsCorporateId')}
            readOnly={!draft.editable('ids.tramsCorporateId')}
            onChange={draft.setText('ids.tramsCorporateId')}
          />
          <SelectField
            label="GDS User"
            value={draft.text('ids.gdsUser')}
            readOnly={!draft.editable('ids.gdsUser')}
            options={GDS_OPTIONS}
            onChange={draft.setText('ids.gdsUser')}
          />
          <ReadOnlyField label="TRIO EPR ID" value={draft.text('ids.trioEpr') || null} />
        </FieldGrid>
        <div className="mt-4 flex items-center gap-3">
          <Switch
            id="trio-enabled"
            checked={draft.bool('ids.trioEnabled')}
            disabled={!draft.editable('ids.trioEnabled')}
            onCheckedChange={(checked) => draft.set('ids.trioEnabled', checked)}
          />
          <Label htmlFor="trio-enabled" className="text-sm text-slate-700 dark:text-slate-300">
            TRIO Account Enabled
          </Label>
        </div>
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          An empty TRIO EPR ID auto-assigns on save while the member is neither suspended nor
          deactivated.
        </p>
      </RecordCard>

      <RecordCard title="Web + Social">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="min-w-[280px] flex-1">
              <TextField
                label="Advisor Showcase Link"
                value={showcaseLink}
                readOnly={!draft.editable('profile.advisorShowcase')}
                onChange={draft.setText('profile.advisorShowcase')}
              />
            </div>
            <div className="flex items-center gap-3 pb-1.5">
              <Switch
                id="showcase-published"
                checked={showcasePublished}
                onCheckedChange={() => setAction('showcase-toggle')}
              />
              <Label htmlFor="showcase-published" className="text-sm text-slate-700 dark:text-slate-300">
                {showcasePublished ? 'Showcase published' : 'Showcase unpublished'}
              </Label>
            </div>
          </div>

          <FieldGrid cols={2}>
            <TextField
              label="WorldVia Website"
              value={draft.text('profile.worldviaWebsite')}
              readOnly={!draft.editable('profile.worldviaWebsite')}
              onChange={draft.setText('profile.worldviaWebsite')}
            />
            <TextField
              label="External Website"
              value={draft.text('profile.website')}
              readOnly={!draft.editable('profile.website')}
              onChange={draft.setText('profile.website')}
            />
            <TextField
              label="Facebook Page"
              value={draft.text('profile.facebook')}
              readOnly={!draft.editable('profile.facebook')}
              onChange={draft.setText('profile.facebook')}
            />
            <TextField
              label="Instagram Page"
              value={draft.text('profile.instagram')}
              readOnly={!draft.editable('profile.instagram')}
              onChange={draft.setText('profile.instagram')}
            />
            <TextField
              label="LinkedIn Page"
              value={draft.text('profile.linkedin')}
              readOnly={!draft.editable('profile.linkedin')}
              onChange={draft.setText('profile.linkedin')}
            />
            <TextField
              label="X (Twitter) Page"
              value={draft.text('profile.twitter')}
              readOnly={!draft.editable('profile.twitter')}
              onChange={draft.setText('profile.twitter')}
            />
          </FieldGrid>

          <div>
            <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">
              Sites &amp; Websites
            </p>
            <div className="flex flex-col gap-2">
              {websites.length === 0 && (
                <p className="text-sm text-slate-500 dark:text-slate-400">No websites on file.</p>
              )}
              {websites.map((url, index) => (
                <TextField
                  key={`website-${index}`}
                  label={`Website ${index + 1}`}
                  value={url}
                  readOnly={!draft.editable('profile.websites')}
                  onChange={(next) => draft.setListItem('profile.websites', index, next)}
                />
              ))}
            </div>
            {draft.editable('profile.websites') && (
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => draft.appendListItem('profile.websites', '')}
              >
                <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
                Add Website
              </Button>
            )}
          </div>
        </div>
      </RecordCard>

      <DeferredAction
        open={action === 'approve-photo'}
        onOpenChange={(open) => !open && setAction(null)}
        title="Approve Showcase photo"
        description="Publishes the pending image to the member's Advisor Showcase profile."
        confirmLabel="Approve photo"
        ticketNote="Showcase image moderation is a companion backend ticket."
      />
      <DeferredAction
        open={action === 'decline-photo'}
        onOpenChange={(open) => !open && setAction(null)}
        title="Decline Showcase photo"
        description="Rejects the pending image and notifies the member to upload a replacement."
        confirmLabel="Decline photo"
        destructive
        fields={[{ name: 'reason', label: 'Reason sent to the member', multiline: true }]}
        ticketNote="Showcase image moderation is a companion backend ticket."
      />
      <DeferredAction
        open={action === 'replace-photo'}
        onOpenChange={(open) => !open && setAction(null)}
        title="Replace profile image"
        description="Upload a PNG or JPG up to 4 MB. The Showcase renders it at 310 × 310."
        confirmLabel="Upload image"
        fields={[{ name: 'file', label: 'Image URL or file reference' }]}
        ticketNote="Image upload and storage is a companion backend ticket."
      />
      <DeferredAction
        open={action === 'status-change'}
        onOpenChange={(open) => {
          if (!open) {
            setAction(null);
            setPendingStatus(null);
          }
        }}
        title={`Set account status to ${pendingStatus ?? ''}`}
        description="A status change sends the member an activation email through Postmark and re-publishes their Advisor Showcase when the account becomes active."
        confirmLabel="Change status"
        destructive={pendingStatus === 'Deactivated' || pendingStatus === 'Suspended'}
        ticketNote="Status transitions and the Postmark email are a companion backend ticket."
      />
      <DeferredAction
        open={action === 'showcase-toggle'}
        onOpenChange={(open) => !open && setAction(null)}
        title={showcasePublished ? 'Unpublish Advisor Showcase' : 'Republish Advisor Showcase'}
        description={
          showcasePublished
            ? 'Removes the member from the public Advisor Showcase, writes an AS Unpublish History record, and emails the member a notice.'
            : 'Restores the member to the public Advisor Showcase, writes an AS Unpublish History record, and emails the member a notice.'
        }
        confirmLabel={showcasePublished ? 'Unpublish' : 'Republish'}
        destructive={showcasePublished}
        ticketNote="Showcase publish state, the history record, and the Bubble sync are a companion backend ticket."
      />
    </div>
  );
}
